import { Form, Modal } from "antd"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { TextInput } from "../../../components/Input/TextInput.tsx"

interface SendInvoiceFormValues {
  recipient: string
}

interface SendInvoiceConfirmModalProps {
  open: boolean
  defaultRecipient?: string | null
  loading?: boolean
  onConfirm: (recipient: string) => void
  onCancel: () => void
}

export const SendInvoiceConfirmModal = ({
  open,
  defaultRecipient,
  loading,
  onConfirm,
  onCancel
}: SendInvoiceConfirmModalProps) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<SendInvoiceFormValues>()

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ recipient: defaultRecipient ?? "" })
    }
  }, [open, defaultRecipient, form])

  const handleOk = async () => {
    // Modal.onOk discards the returned promise, so a validateFields()
    // rejection would escape as an unhandled rejection.
    try {
      const values = await form.validateFields()
      onConfirm(values.recipient.trim())
    } catch {
      // Field-level errors are rendered by the Form.
    }
  }

  return (
    <Modal
      open={open}
      title={t("invoices.confirmSend.title")}
      okText={t("invoices.confirmSend.ok")}
      cancelText={t("invoices.confirmSend.cancel")}
      confirmLoading={loading}
      onOk={handleOk}
      onCancel={onCancel}
      destroyOnClose
    >
      <Form<SendInvoiceFormValues> form={form} layout="vertical">
        <Form.Item<SendInvoiceFormValues>
          name="recipient"
          label={t("invoices.confirmSend.recipient")}
          rules={[
            { required: true, message: t("invoices.confirmSend.recipientRequired") },
            { type: "email", message: t("invoices.confirmSend.recipientInvalid") }
          ]}
        >
          <TextInput
            disableAutoCapitalize
            placeholder={t("invoices.confirmSend.recipientPlaceholder")}
            autoFocus
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
